import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getRequestById,
  updateRequestForPayment,
} from "../redux/action/request";
import Header from "./Header";
import Footur from "./footur";
import DownloadIcon from "../assets/icons/download";
import Green from "../assets/icons/green";
import Gray from "../assets/icons/Gray";
import Confetti from "react-confetti";

const RequestDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { requestById } = useSelector((state) => state.auth);

  // State management
  const [showConfetti, setShowConfetti] = useState(true);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState([]);

  // Hide confetti after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Fetch request data
  useEffect(() => {
    dispatch(getRequestById(id));
  }, [dispatch, id]);

  // Set preview when request data loads
  useEffect(() => {
    setPreview(requestById?.paymentScreenShot || "");
  }, [requestById]);

  // Setup steps progress
  useEffect(() => {
    setSteps([
      { label: "Request Sent", status: requestById?.requestSent },
      { label: "Quotation Made", status: requestById?.quotationMade },
      { label: "Payment Done", status: requestById?.paymentDone },
      { label: "Policy Uploaded", status: requestById?.policyUploaded },
    ]);
  }, [requestById]);

  // Handle file upload
  const handleFileChange = async (file, index) => {
    if (!file) return;

    try {
      setLoading(true);
      const base64String = await convertToBase64(file);
      setPreview(base64String);

      await dispatch(
        updateRequestForPayment(id, {
          paymentScreenShot: base64String,
          quotationClicked: index,
        })
      );

      setTimeout(() => dispatch(getRequestById(id)), 1000);
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setLoading(false);
    }
  };

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle file drop
  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) handleFileChange(file, requestById?.quotationClicked);
  };

  // Handle download policy
  const handleDownload = () => {
    if (!requestById?.policyUrl) return;

    try {
      const link = document.createElement("a");
      link.href = requestById.policyUrl;
      link.download = "policy_document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  // Handle payment
  const handlePayment = (plan, index) => {
    dispatch(updateRequestForPayment(id, { quotationClicked: index }));
    setTimeout(() => {
      dispatch(getRequestById(id));
      if (plan?.paymentLink) {
        window.open(plan.paymentLink, "_blank");
      }
    }, 2000);
  };

  // Dynamic height calculation
  const getHeightClass = () => {
    if (requestById?.paymentDone) return "h-[580px]";
    if (requestById?.quotationMade) return "h-[530px]";
    if (requestById?.requestSent) return "h-[590px]";
    return "h-auto";
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <Header />

      {/* Confetti (only shows when policy is uploaded) */}
      {requestById?.policyUploaded && showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 text-blue-600 hover:text-blue-800 flex items-center"
        >
          ← Back
        </button>

        {/* Progress steps */}
        <div className="flex justify-between items-center mb-12 relative">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center z-10">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    step.status ? "bg-green-500" : "bg-gray-200"
                  }`}
                >
                  {step.status ? (
                    <Green className="text-white w-5 h-5" />
                  ) : (
                    <Gray className="text-gray-500 w-5 h-5" />
                  )}
                </div>
                <p className="text-xs mt-2 text-gray-600">{step.label}</p>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`absolute h-1 top-5 ${
                    step.status ? "bg-green-500" : "bg-gray-200"
                  }`}
                  style={{ width: "calc(100% - 160px)", left: "80px" }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left sidebar - Vehicle and owner info */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4">
              {requestById?.vehicleModel || "Vehicle Details"}
            </h2>

            <div className="space-y-3">
              <p className="text-sm">
                <span className="font-medium">Reg No:</span>{" "}
                {requestById?.registerNumber || "N/A"}
              </p>
              <p className="text-sm">
                <span className="font-medium">Make:</span>{" "}
                {requestById?.make || "N/A"}
              </p>
              <p className="text-sm">
                <span className="font-medium">Manufacture:</span>{" "}
                {requestById?.manufactureDate
                  ? new Date(requestById.manufactureDate).toLocaleDateString()
                  : "N/A"}
              </p>
              <p className="text-sm">
                <span className="font-medium">Fuel:</span>{" "}
                {requestById?.fuelType || "N/A"}
              </p>
              <p className="text-sm">
                <span className="font-medium">Policy Type:</span>{" "}
                {requestById?.policyType || "N/A"}
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-medium text-blue-600 mb-2">Owner Details</h3>
              <p className="text-sm">
                {requestById?.ownerDetail?.ownerName || "N/A"}
              </p>
              <p className="text-sm">
                {requestById?.ownerDetail?.ownerPhone || "N/A"}
              </p>
              <p className="text-sm">
                {requestById?.ownerDetail?.ownerAddress || "N/A"}
              </p>
              <p className="text-sm text-blue-500">
                {requestById?.ownerDetail?.ownerEmail || "N/A"}
              </p>
            </div>

            {requestById?.paymentDetail && (
              <div className="mt-6">
                <h3 className="font-medium text-blue-600 mb-2">
                  Payment Details
                </h3>
                <div className="flex items-center space-x-2 mb-2">
                  {requestById.paymentDetail.companyLogo && (
                    <img
                      src={requestById.paymentDetail.companyLogo}
                      alt="Company"
                      className="w-8 h-8 rounded-full"
                    />
                  )}
                  <span>
                    {requestById.paymentDetail.companyName ||
                      "Insurance Company"}
                  </span>
                </div>
                <p className="text-2xl font-light">
                  ₹{requestById.paymentDetail.premiumAmount || "0"}
                </p>
                <p
                  className={`text-sm mt-2 px-2 py-1 inline-block rounded ${
                    requestById?.paymentVarified
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {requestById?.paymentVarified ? "Paid" : "Pending"}
                </p>
              </div>
            )}
          </div>

          {/* Main content area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quotation section */}
            {requestById?.quotationMade && !requestById?.paymentDone && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {requestById?.quotationDetail?.map((plan, index) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-lg shadow-sm border"
                  >
                    <div className="flex items-center mb-3">
                      {plan?.companyLogo && (
                        <img
                          src={plan.companyLogo}
                          alt={plan.companyName}
                          className="w-10 h-10 mr-3 rounded-full"
                        />
                      )}
                      <h4 className="font-medium">
                        {plan?.companyName || "Insurance Provider"}
                      </h4>
                    </div>

                    <div className="space-y-2 text-sm mb-4">
                      <p>
                        <span className="text-black font-bold">Cover :</span>{" "}
                        <span>{plan?.coverAmount || "N/A"}</span>
                      </p>
                      <p>
                        <span className="text-black font-bold">Premium:</span>{" "}
                        <span>{plan?.premiumAmount || "N/A"}</span>
                      </p>
                      <p>
                        <span className="text-black font-bold">Activity Points:</span>
                        <span> {plan?.activityPoints || "0"}</span>
                      </p>
                      {plan?.cashlessCharge && (
                        <a
                          href={plan.cashlessCharge}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline block"
                        >
                          View Cashless Garages
                        </a>
                      )}
                    </div>

                    <button
                      onClick={() => handlePayment(plan, index)}
                      className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Pay Now
                    </button>

                    {requestById?.quotationClicked === index && (
                      <div className="mt-4 border-t pt-4">
                        {preview ? (
                          <div className="text-center">
                            <img
                              src={preview}
                              alt="Payment proof"
                              className="w-32 h-32 mx-auto mb-2 border"
                            />
                            <p className="text-sm text-gray-500 mb-2">
                              Under review
                            </p>
                            <button
                              onClick={() => setPreview("")}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <div
                            className="text-center border-2 border-dashed p-4 rounded"
                            onDrop={handleDrop}
                            onDragOver={(e) => e.preventDefault()}
                          >
                            <label className="block text-sm mb-2">
                              Upload payment proof
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(e.target.files?.[0], index)
                                }
                                className="hidden"
                                id={`fileInput-${index}`}
                              />
                            </label>
                            <label
                              htmlFor={`fileInput-${index}`}
                              className="block w-full text-sm text-gray-500
                                file:mr-4 file:py-2 file:px-4
                                file:rounded file:border-0
                                file:text-sm file:font-semibold
                                file:bg-blue-50 file:text-blue-700
                                hover:file:bg-blue-100 cursor-pointer"
                            >
                              Click to select file
                            </label>
                            {loading && (
                              <p className="text-sm mt-2 text-blue-600">
                                Uploading...
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Quotation pending */}
            {!requestById?.quotationMade && requestById?.requestSent && (
              <div className="bg-white p-6 rounded-lg shadow-sm text-center">
                <p className="text-gray-600">
                  Your quotation is being prepared. We'll notify you when it's
                  ready.
                </p>
              </div>
            )}

            {/* Payment done, policy pending */}
            {requestById?.paymentDone && !requestById?.policyUploaded && (
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-medium text-green-600 mb-2">
                  Payment Successful
                </h3>
                <p className="text-gray-600 mb-4">
                  Your policy is being processed. We'll notify you once it's
                  ready.
                </p>
                <div className="bg-blue-50 p-4 rounded">
                  <p className="font-medium text-sm mb-2">Next steps:</p>
                  <p className="text-sm text-gray-600">
                    Please share PAN and Aadhaar via WhatsApp at 9026795871 to
                    complete KYC.
                  </p>
                </div>
              </div>
            )}

            {/* Policy ready */}
            {requestById?.paymentDone && requestById?.policyUploaded && (
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-medium text-green-600 mb-2">
                  Policy Ready
                </h3>
                <p className="text-gray-600 mb-4">
                  Your policy document has been successfully generated.
                </p>

                <div className="space-y-4">
                  <button
                    onClick={handleDownload}
                    className="flex items-center justify-center w-full md:w-auto px-4 py-2 bg-yellow-400 text-black rounded hover:bg-yellow-500"
                  >
                    <DownloadIcon className="w-5 h-5 mr-2" />
                    Download Policy
                  </button>

                  {requestById?.policyExpire && (
                    <p className="text-sm text-gray-500">
                      Expires on:{" "}
                      {new Date(requestById.policyExpire).toLocaleDateString()}
                    </p>
                  )}

                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-2">Need help?</p>
                    <p className="text-sm text-gray-600">
                      Contact our support team for any questions about your
                      policy.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestDetail;
